import { WeightedAstar } from "../algorithms/WeightedAstar.js";
import { Channel } from "../misc/Channel.js";
import { CandidateTrajectory } from "./CandidateTrajectory.js";
import { SearchNode } from "./SearchNode.js";
import { SearchSpace } from "./SearchSpace.js";
import { ActiveGraph } from "./ActiveGraph.js";
import { EndTrajectory } from "./EndTrajectory.js";
import { ChannelPath } from "../misc/ChannelPath.js";
import { ChannelPathArc } from "../misc/ChannelPathArc.js";
import { FullTrajectory } from "./FullTrajectory.js";
import { cloneMandatoryWaypointStateMap, MandatoryWaypointState } from "../sequences/MandatoryWaypointState.js";
import { RoutingResult } from './RoutingResult.js';
import { MandatoryWaypointSequence } from '../sequences/MandatoryWaypointSequence.js';
import { MandatoryWaypoint } from '../sequences/MandatoryWaypoint.js';
import { Meander } from "./Meander.js";
import { VisitedNodes } from "./VisitedNodes.js";
import { RTreeID } from "./RTreeID.js";
export { Router };
class Router {
    static route({ connections, obstacles }) {
        const searchSpace = SearchSpace.of({ connections, obstacles });
        const activeGraph = ActiveGraph.create();
        const result = Router.routeConnections({
            connections,
            searchSpace,
            activeGraph
        });
        return result.routings;
    }
    static routeAndExtend({ connections, obstacles }) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const targetFound = function (length, targetLength) {
            return Math.abs(length - targetLength) < 1e-1;
        };
        const searchSpace = SearchSpace.of({ connections, obstacles });
        const activeGraph = ActiveGraph.create();
        const basicRouting = Router.routeConnections({
            connections,
            searchSpace,
            activeGraph
        });
        if (![...basicRouting.routings.values()].every(r => r.routingFound)) {
            return basicRouting.routings;
        }
        let currentResults = basicRouting;
        let currentSearchSpace = searchSpace;
        let currentMandatoryWaypointStates = new Map();
        const endTrajectories = Router.getEndTrajectories({
            activeGraph: currentResults.activeGraph
        });
        [...currentResults.routings.values()].forEach((routing) => {
            const endTrajectory = Router.findEndTrajectoriesOfConnection({ endTrajectories, connection: routing.connection }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
            currentMandatoryWaypointStates.set(routing.connection.id, new MandatoryWaypointState({
                mandatoryWaypoints: new MandatoryWaypointSequence(Router.getConnectionWaypoints({
                    endTrajectory
                }).map(w => new MandatoryWaypoint(w)))
            }));
        });
        let currentActiveGraph = currentResults.activeGraph;
        for (let connection of connections) {
            currentActiveGraph = currentResults.activeGraph;
            const activeWithoutConnection = currentActiveGraph.clone().activeGraph;
            Router.removeConnection({ searchSpace, activeGraph: activeWithoutConnection, connection });
            let shorterRouting = undefined;
            let longerResults = {
                routings: undefined,
                activeGraph: undefined
            };
            const currentRouting = currentResults.routings.get(connection.id);
            if (currentRouting === undefined || currentRouting.channel === undefined) {
                continue;
            }
            if (!connection.modifyLength || connection.length === undefined) {
                continue;
            }
            if (currentRouting.channel.length >= connection.length) {
                longerResults = currentResults;
                continue;
            }
            else {
                shorterRouting = currentResults.routings;
            }
            const endTrajectories = Router.getEndTrajectories({
                activeGraph: currentResults.activeGraph
            });
            const endTrajectory = Router.findEndTrajectoriesOfConnection({
                endTrajectories,
                connection
            }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
            const meanders = [];
            const meanderArmMaxed = [];
            let previous = endTrajectory;
            const waypoints = [previous.waypoint.id];
            let current = endTrajectory.tangent.getOtherTrajectory(endTrajectory);
            while (!(current instanceof EndTrajectory)) {
                if (!(current instanceof FullTrajectory)) {
                    throw new Error('Unexpected case');
                }
                const previousTangent = current.getTangentTo(previous);
                const nextTangent = current.getOtherTangent(previousTangent);
                const meander = Meander.createMeander({
                    startPoint: previousTangent.getOtherPoint(current),
                    endPoint: previousTangent.getPoint(current),
                    width: connection.width,
                    spacing: connection.spacing,
                    minBendradius: connection.minBendradius,
                    epsilon: 1e-6,
                    armLengthCallBack: () => Meander.regularRadius({
                        width: connection.width,
                        spacing: connection.spacing,
                        minBendradius: connection.minBendradius
                    })
                });
                meanders.push(meander);
                meanderArmMaxed.push(meander === undefined ? [] : meander.arms.map(_ => false));
                waypoints.push(current.waypoint.id);
                previous = current;
                current = nextTangent.getOtherTrajectory(current);
            }
            const lastTrajectory = current;
            const lastTangent = lastTrajectory.tangent;
            const lastMeander = Meander.createMeander({
                startPoint: lastTangent.getOtherPoint(current),
                endPoint: lastTangent.getPoint(current),
                width: connection.width,
                spacing: connection.spacing,
                minBendradius: connection.minBendradius,
                epsilon: 1e-6,
                armLengthCallBack: () => Meander.regularRadius({
                    width: connection.width,
                    spacing: connection.spacing,
                    minBendradius: connection.minBendradius
                })
            });
            meanders.push(lastMeander);
            meanderArmMaxed.push(lastMeander === undefined ? [] : lastMeander.arms.map(_ => false));
            waypoints.push(lastTrajectory.waypoint.id);
            meanders.forEach(meander => {
                if (meander !== undefined) {
                    meander.arms = meander.arms.map(a => { return Object.assign(Object.assign({}, a), { length: undefined }); });
                }
            });
            const connectionLength = (_b = (_a = currentResults.routings.get(connection.id)) === null || _a === void 0 ? void 0 : _a.channel) === null || _b === void 0 ? void 0 : _b.length;
            if (connectionLength === undefined) {
                throw new Error('Unexpected case');
            }
            let currentConnectionLength = connectionLength;
            let i = 0;
            let j = 0;
            if (meanders.length === 0) {
                throw new Error('Unexpected case');
            }
            let shorterMeanders = meanders.map(m => m === undefined ? undefined : m.clone());
            let longerMeanders = undefined;
            while (true) {
                if (i === meanders.length) {
                    if (meanderArmMaxed.every(m => m.every(a => a))) {
                        break;
                    }
                    i = 0;
                    continue;
                }
                const currentMeander = meanders[i];
                if (currentMeander === undefined || j === currentMeander.arms.length) {
                    j = 0;
                    i++;
                    continue;
                }
                if (currentMeander !== undefined && meanderArmMaxed[i][j]) {
                    j++;
                    continue;
                }
                const step = currentMeander.regularRadius;
                const currentArm = currentMeander.arms[j];
                const previousArmLength = currentArm.length;
                if (currentArm.length === undefined) {
                    currentArm.length = step;
                }
                else {
                    currentArm.length += step;
                }
                const meanderConnectionConfiguration = Router.meanderConnectionConfiguration({
                    meanders,
                    connectionWaypoints: waypoints,
                    searchSpace: currentSearchSpace
                });
                const mandatoryWaypointStates = cloneMandatoryWaypointStateMap(currentMandatoryWaypointStates);
                mandatoryWaypointStates.set(connection.id, meanderConnectionConfiguration.mandatoryWaypointState);
                const resultSearchSpace = meanderConnectionConfiguration.searchSpace;
                /*const result = Router.routeConnections({
                  connections,
                  searchSpace: resultSearchSpace,
                  activeGraph: ActiveGraph.create(),
                  mandatoryWaypoints: mandatoryWaypointStates
                })*/
                const path = Router.routeConnection({
                    connection,
                    searchSpace: resultSearchSpace,
                    activeGraph: activeWithoutConnection,
                    mandatoryWaypoints: mandatoryWaypointStates.get(connection.id)
                });
                //if([...result.routings.values()].every(r => r.routingFound)) {
                if (path !== undefined) {
                    const result = Router.getRoutingResult({
                        connection,
                        activeGraph: path[path.length - 1].activeGraph
                    });
                    if (result.routingFound) {
                        const results = {
                            routings: new Map(currentResults.routings),
                            activeGraph: path[path.length - 1].activeGraph
                        };
                        results.routings.set(connection.id, result);
                        const connectionLength = (_d = (_c = results.routings.get(connection.id)) === null || _c === void 0 ? void 0 : _c.channel) === null || _d === void 0 ? void 0 : _d.length;
                        if (connectionLength === undefined) {
                            throw new Error("Unexpected case");
                        }
                        currentConnectionLength = connectionLength;
                        currentResults = results;
                        if (currentConnectionLength >= connection.length) {
                            longerResults = currentResults;
                            longerMeanders = meanders.map(m => m === undefined ? undefined : m.clone());
                            const shorterConnectionLength = (_f = (_e = shorterRouting.get(connection.id)) === null || _e === void 0 ? void 0 : _e.channel) === null || _f === void 0 ? void 0 : _f.length;
                            const longerConnectionLength = (_h = (_g = longerResults.routings.get(connection.id)) === null || _g === void 0 ? void 0 : _g.channel) === null || _h === void 0 ? void 0 : _h.length;
                            if (shorterConnectionLength === undefined || longerConnectionLength === undefined) {
                                throw new Error('Unexpected case');
                            }
                            const bisectionResult = Router.meanderBisection({
                                connections,
                                connection,
                                connectionWaypoints: waypoints,
                                shorterMeanders,
                                longerMeanders,
                                shorterConnectionLength,
                                longerConnectionLength,
                                differenceMeander: i,
                                differenceArm: j,
                                currentResults,
                                searchSpace: currentSearchSpace,
                                mandatoryWaypointStates: currentMandatoryWaypointStates
                            });
                            if (bisectionResult !== undefined) {
                                const meanderConnectionConfiguration = Router.meanderConnectionConfiguration({
                                    meanders: bisectionResult,
                                    connectionWaypoints: waypoints,
                                    searchSpace: currentSearchSpace
                                });
                                const mandatoryWaypointStates = cloneMandatoryWaypointStateMap(currentMandatoryWaypointStates);
                                mandatoryWaypointStates.set(connection.id, meanderConnectionConfiguration.mandatoryWaypointState);
                                const resultSearchSpace = meanderConnectionConfiguration.searchSpace;
                                /*const result = Router.routeConnections({
                                  connections,
                                  searchSpace: resultSearchSpace,
                                  activeGraph: ActiveGraph.create(),
                                  mandatoryWaypoints: mandatoryWaypointStates
                                })*/
                                const path = Router.routeConnection({
                                    connection,
                                    searchSpace: resultSearchSpace,
                                    activeGraph: activeWithoutConnection,
                                    mandatoryWaypoints: mandatoryWaypointStates.get(connection.id)
                                });
                                /*if(![...result.routings.values()].every(r => r.routingFound)) {
                                  throw new Error('Unexpected case. Have fun debugging this...')
                                }*/
                                if (path === undefined) {
                                    throw new Error('Unexpected case. Have fun debugging this...');
                                }
                                const result = Router.getRoutingResult({
                                    connection,
                                    activeGraph: path[path.length - 1].activeGraph
                                });
                                if (!result.routingFound) {
                                    throw new Error('Unexpected case 2. Have fun debugging this...');
                                }
                                const results = {
                                    routings: new Map(currentResults.routings),
                                    activeGraph: path[path.length - 1].activeGraph
                                };
                                results.routings.set(connection.id, result);
                                currentResults = results;
                            }
                            else {
                                currentResults = longerResults;
                            }
                            break;
                        }
                        else {
                            shorterRouting = currentResults.routings;
                            shorterMeanders = meanders.map(m => m === undefined ? undefined : m.clone());
                        }
                    }
                    else {
                        currentArm.length = previousArmLength;
                        meanderArmMaxed[i][j] = true;
                    }
                }
                else {
                    currentArm.length = previousArmLength;
                    meanderArmMaxed[i][j] = true;
                }
                j++;
            }
            const r = currentResults.routings.get(connection.id);
            if (r === undefined) {
                throw new Error('Unexpected case');
            }
            const finalEndTrajectories = Router.getEndTrajectories({
                activeGraph: currentResults.activeGraph
            });
            const newEndTrajectory = Router.findEndTrajectoriesOfConnection({ endTrajectories: finalEndTrajectories, connection: r.connection }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
            const newWaypoints = Router.getConnectionWaypointsEmptyClones({
                endTrajectory: newEndTrajectory
            });
            currentMandatoryWaypointStates.set(r.connection.id, new MandatoryWaypointState({
                mandatoryWaypoints: new MandatoryWaypointSequence(newWaypoints.map(w => new MandatoryWaypoint(w.id)))
            }));
            currentSearchSpace = currentSearchSpace.withAdditionalWaypointsUnique({ waypoints: newWaypoints });
        }
        for (let [_, routingResult] of currentResults.routings) {
            if (routingResult.connection.modifyLength && routingResult.connection.length !== undefined) {
                if (routingResult.channel !== undefined && targetFound(routingResult.channel.length, routingResult.connection.length)) {
                    routingResult.routingWithLengthFound = true;
                }
            }
        }
        return currentResults.routings;
    }
    static meanderConnectionConfiguration({ meanders, connectionWaypoints, searchSpace }) {
        const meanderWaypoints = [];
        const waypoints = [];
        for (let i = 0; i < connectionWaypoints.length; i++) {
            waypoints.push(connectionWaypoints[i]);
            const m = meanders[i];
            if (m !== undefined) {
                const mWaypoints = m.getWaypoints();
                waypoints.push(...mWaypoints.map(w => w.id));
                meanderWaypoints.push(...mWaypoints);
            }
        }
        const mandatoryWaypointState = new MandatoryWaypointState({
            mandatoryWaypoints: new MandatoryWaypointSequence(waypoints.map(w => new MandatoryWaypoint(w)))
        });
        const newSearchSpace = searchSpace.withAdditionalWaypoints({
            waypoints: meanderWaypoints
        });
        return {
            searchSpace: newSearchSpace,
            mandatoryWaypointState
        };
    }
    static getRoutingResult({ connection, activeGraph }) {
        const endTrajectories = Router.getEndTrajectories({
            activeGraph
        });
        const endTrajectory = Router.findEndTrajectoriesOfConnection({ endTrajectories, connection }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
        if (endTrajectory === undefined) {
            return new RoutingResult({
                connection,
                routingFound: false,
                routingWithLengthFound: false
            });
        }
        const channelPath = Router.computeChannelPath({ endTrajectory });
        const channel = new Channel({
            path: channelPath,
            width: connection.width
        });
        return new RoutingResult({
            connection,
            routingFound: true,
            routingWithLengthFound: false,
            channel
        });
    }
    static meanderBisection({ connections, connection, connectionWaypoints, shorterMeanders, longerMeanders, shorterConnectionLength, longerConnectionLength, differenceMeander, differenceArm, searchSpace, currentResults, mandatoryWaypointStates }) {
        var _a, _b, _c, _d, _e;
        const armEpsilon = 1e-6;
        const shorterArm = (_a = shorterMeanders[differenceMeander]) === null || _a === void 0 ? void 0 : _a.arms[differenceArm];
        let shorterArmLength = (_b = shorterMeanders[differenceMeander]) === null || _b === void 0 ? void 0 : _b.arms[differenceArm].length;
        let longerArmLength = (_c = longerMeanders[differenceMeander]) === null || _c === void 0 ? void 0 : _c.arms[differenceArm].length;
        if (longerArmLength === undefined || shorterArm === undefined) {
            return undefined;
        }
        if (connection.length === undefined) {
            return undefined;
        }
        const targetLength = connection.length;
        let shorterLength = shorterConnectionLength;
        let longerLength = longerConnectionLength;
        const bendRadius = shorterMeanders[differenceMeander].regularRadius;
        let candidateArmLength = shorterArmLength !== undefined ? 0.5 * (shorterArmLength + longerArmLength) : 0.5 * (longerArmLength - bendRadius);
        const targetDistance = function (length) {
            return Math.abs(length - targetLength);
        };
        const targetCondition = function (length) {
            return targetDistance(length) <= 1e-1;
        };
        const activeWithoutConnection = currentResults.activeGraph.clone().activeGraph;
        Router.removeConnection({ searchSpace, activeGraph: activeWithoutConnection, connection });
        while (true) {
            const candidateMeanders = shorterMeanders.map(m => m === undefined ? undefined : m.clone());
            candidateMeanders[differenceMeander].arms[differenceArm].length = candidateArmLength;
            const meanderConnectionConfiguration = Router.meanderConnectionConfiguration({
                meanders: candidateMeanders,
                connectionWaypoints,
                searchSpace
            });
            const candidateMandatoryWaypointStates = cloneMandatoryWaypointStateMap(mandatoryWaypointStates);
            candidateMandatoryWaypointStates.set(connection.id, meanderConnectionConfiguration.mandatoryWaypointState);
            /*const result = Router.routeConnections({
              connections,
              searchSpace: meanderConnectionConfiguration.searchSpace,
              activeGraph: ActiveGraph.create(),
              mandatoryWaypoints: candidateMandatoryWaypointStates
            })*/
            const path = Router.routeConnection({
                connection,
                searchSpace: meanderConnectionConfiguration.searchSpace,
                activeGraph: activeWithoutConnection,
                mandatoryWaypoints: candidateMandatoryWaypointStates.get(connection.id)
            });
            if (path !== undefined) {
                const result = Router.getRoutingResult({
                    connection,
                    activeGraph: path[path.length - 1].activeGraph
                });
                //if([...result.routings.values()].every(r => r.routingFound)) {
                if (result.routingFound) {
                    const results = {
                        routings: new Map(currentResults.routings),
                        activeGraph: path[path.length - 1].activeGraph
                    };
                    results.routings.set(connection.id, result);
                    const candidateLength = (_e = (_d = results.routings.get(connection.id)) === null || _d === void 0 ? void 0 : _d.channel) === null || _e === void 0 ? void 0 : _e.length;
                    if (candidateLength === undefined) {
                        throw new Error("Unexpected case");
                    }
                    if (candidateLength < shorterLength) {
                        return shorterMeanders;
                    }
                    else if (candidateLength > longerLength) {
                        return longerMeanders;
                    }
                    else if (targetCondition(candidateLength)) {
                        return candidateMeanders;
                    }
                    else if (Math.abs((shorterArmLength !== null && shorterArmLength !== void 0 ? shorterArmLength : -bendRadius) - longerArmLength) <= armEpsilon) {
                        return targetDistance(shorterLength) < targetDistance(longerLength) ? shorterMeanders : longerMeanders;
                    }
                    else if (candidateLength < targetLength) {
                        shorterArmLength = candidateArmLength;
                        shorterLength = candidateLength;
                        shorterMeanders = candidateMeanders;
                    }
                    else if (candidateLength > targetLength) {
                        longerArmLength = candidateArmLength;
                        longerLength = candidateLength;
                        longerMeanders = candidateMeanders;
                    }
                    else {
                        throw new Error('Unexpected case');
                    }
                }
                else {
                    return targetDistance(shorterLength) < targetDistance(longerLength) ? shorterMeanders : longerMeanders;
                }
            }
            else {
                return targetDistance(shorterLength) < targetDistance(longerLength) ? shorterMeanders : longerMeanders;
            }
            candidateArmLength = shorterArmLength !== undefined ? 0.5 * (shorterArmLength + longerArmLength) : 0.5 * (longerArmLength - bendRadius);
        }
    }
    static routeConnections({ connections, searchSpace, activeGraph, mandatoryWaypoints }) {
        var _a;
        const results = new Map();
        const mandatoryWaypointsState = mandatoryWaypoints !== null && mandatoryWaypoints !== void 0 ? mandatoryWaypoints : new Map();
        let current = activeGraph;
        for (let connection of connections) {
            const path = Router.routeConnection({
                searchSpace,
                activeGraph: current,
                connection,
                mandatoryWaypoints: (_a = mandatoryWaypointsState.get(connection.id)) !== null && _a !== void 0 ? _a : MandatoryWaypointState.none()
            });
            if (path === undefined) {
                searchSpace.activeGraph = current;
            }
            else {
                current = path[path.length - 1].activeGraph;
                searchSpace.activeGraph = current;
            }
        }
        const endTrajectories = Router.getEndTrajectories({
            activeGraph: current
        });
        for (let connection of connections) {
            const endTrajectory = Router.findEndTrajectoriesOfConnection({ endTrajectories, connection }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
            if (endTrajectory === undefined) {
                results.set(connection.id, new RoutingResult({
                    connection,
                    routingFound: false,
                    routingWithLengthFound: false
                }));
                continue;
            }
            const channelPath = Router.computeChannelPath({ endTrajectory });
            const channel = new Channel({
                path: channelPath,
                width: connection.width
            });
            results.set(connection.id, new RoutingResult({
                connection,
                routingFound: true,
                routingWithLengthFound: false,
                channel,
            }));
        }
        return {
            routings: results,
            activeGraph: current
        };
    }
    static removeConnection({ searchSpace, activeGraph, connection }) {
        const endTrajectories = Router.getEndTrajectories({
            activeGraph
        });
        const endTrajectory = Router.findEndTrajectoriesOfConnection({ endTrajectories, connection }).sort((a, b) => a.waypoint.id - b.waypoint.id)[0];
        if (endTrajectory === undefined) {
            return;
        }
        const wpids = Router.getConnectionWaypoints({ endTrajectory });
        const wp = wpids.map(w => activeGraph.waypoints.get(w));
        if (wp.some(w => w === undefined)) {
            throw 'remove error';
        }
        const removedTangents = [];
        wp.forEach(w => {
            const trajectory = w.trajectories.find(t => t.connection.id === connection.id);
            if (trajectory === undefined) {
                throw 'remove error';
            }
            const tangents = trajectory.getTangents();
            tangents.forEach(t => {
                if (!removedTangents.includes(t)) {
                    removedTangents.push(t);
                    activeGraph.removeTangent(t);
                }
            });
            if (w.trajectories.length === 1) {
                activeGraph.waypoints.delete(w.id);
                activeGraph.waypointsTree.remove(new RTreeID({
                    id: w.id,
                    minX: w.minX,
                    maxX: w.maxX,
                    minY: w.minY,
                    maxY: w.maxY
                }), (a, b) => a.id === b.id);
            }
            w.trajectories.splice(w.trajectories.indexOf(trajectory), 1);
        });
    }
    static routeConnection({ searchSpace, activeGraph, mandatoryWaypoints, connection }) {
        const permittedWaypoints = (mandatoryWaypoints !== null && mandatoryWaypoints !== void 0 ? mandatoryWaypoints : MandatoryWaypointState.none()).clone();
        const startPort = connection.ports[0], endPort = connection.ports[1];
        searchSpace.activeGraph = activeGraph;
        const visitedNodes = VisitedNodes.create();
        const startNodes = SearchNode.startWaypoints({
            searchSpace,
            activeGraph,
            mandatoryWaypointsState: permittedWaypoints,
            port: startPort,
            connection
        });
        if (startNodes === undefined) {
            return undefined;
        }
        const search = WeightedAstar.shortestPath({
            startNodes,
            isTarget: (node, astarNode) => node.candidateTrajectory instanceof EndTrajectory,
            successors: (node, astarNode) => {
                var _a;
                const successors = [];
                searchSpace.activeGraph = node.activeGraph;
                const prepareNeighbor = function (neighbor) {
                    const results = [];
                    const waypoint = searchSpace.activeWaypoints.get(neighbor);
                    if (waypoint === undefined) {
                        throw new Error('Unexpected case');
                    }
                    if (waypoint.isPortWaypoint({ port: endPort, connection })) {
                        const nodes = SearchNode.endPortWaypoint({ searchSpace, current: node, waypoint, port: endPort, connection, visitedNodes });
                        if (nodes !== undefined) {
                            results.push(...nodes);
                        }
                    }
                    const nodes = SearchNode.waypoint({
                        searchSpace,
                        current: node,
                        waypoint,
                        connection,
                        visitedNodes
                    });
                    if (nodes !== undefined) {
                        results.push(...nodes);
                    }
                    return results;
                };
                if (node.candidateTrajectory.waypoint.isPortWaypoint({ port: endPort, connection }) && node.candidateTrajectory.isOnOrbit({ point: endPort.position, direction: endPort.direction }) && node.candidateTrajectory instanceof CandidateTrajectory && Math.sign(node.candidateTrajectory.rotation) !== Math.sign(node.candidateTrajectory.waypoint.rotationOfPoint({ point: endPort.position, direction: endPort.direction }))) {
                    const neighbor = SearchNode.endTrajectory({ searchSpace, current: node, port: endPort, connection });
                    if (neighbor !== undefined) {
                        successors.push(neighbor);
                    }
                }
                else {
                    const nextMandatory = node.mandatoryWaypointsState.next();
                    let neighbors;
                    if (nextMandatory !== undefined) {
                        neighbors = [nextMandatory];
                    }
                    else {
                        neighbors = (_a = searchSpace.visibility.get(node.candidateTrajectory.waypoint.id)) !== null && _a !== void 0 ? _a : [];
                    }
                    successors.push(...neighbors.flatMap(n => prepareNeighbor(n)));
                }
                return successors;
            },
            heuristic: (node, astarNode) => {
                return Math.hypot(node.candidateTrajectory.point[0] - endPort.position[0], node.candidateTrajectory.point[1] - endPort.position[1]);
            },
            epsilon: 1.2
        });
        return search;
    }
    static getEndTrajectories({ activeGraph }) {
        return [...activeGraph.waypoints.values()].flatMap(w => {
            return w.trajectories.filter(t => t instanceof EndTrajectory);
        });
    }
    static findEndTrajectoriesOfConnection({ endTrajectories, connection }) {
        return endTrajectories.filter(trajectory => trajectory.connection === connection);
    }
    static getConnectionWaypointsEmptyClones({ endTrajectory }) {
        const waypoints = [];
        let previous = endTrajectory;
        waypoints.push(previous.waypoint);
        let current = endTrajectory.tangent.getOtherTrajectory(endTrajectory);
        while (!(current instanceof EndTrajectory)) {
            if (!(current instanceof FullTrajectory)) {
                throw new Error('Unexpected case');
            }
            waypoints.push(current.waypoint.emptyClone());
            const nextTangent = current.getOtherTangent(current.getTangentTo(previous));
            previous = current;
            current = nextTangent.getOtherTrajectory(current);
        }
        waypoints.push(current.waypoint);
        return waypoints;
    }
    static getConnectionWaypoints({ endTrajectory }) {
        const waypoints = [];
        let previous = endTrajectory;
        waypoints.push(previous.waypoint.id);
        let current = endTrajectory.tangent.getOtherTrajectory(endTrajectory);
        while (!(current instanceof EndTrajectory)) {
            if (!(current instanceof FullTrajectory)) {
                throw new Error('Unexpected case');
            }
            waypoints.push(current.waypoint.id);
            const nextTangent = current.getOtherTangent(current.getTangentTo(previous));
            previous = current;
            current = nextTangent.getOtherTrajectory(current);
        }
        waypoints.push(current.waypoint.id);
        return waypoints;
    }
    static computeChannelPath({ endTrajectory, }) {
        const arcs = [];
        arcs.push(new ChannelPathArc({
            startPoint: endTrajectory.point,
            startDirection: endTrajectory.direction,
            endPoint: endTrajectory.tangent.getPoint(endTrajectory),
            endDirection: endTrajectory.tangent.getDirection(endTrajectory),
            center: endTrajectory.center,
            radius: endTrajectory.radius,
            rotation: endTrajectory.rotation
        }));
        let previous = endTrajectory;
        let current = endTrajectory.tangent.getOtherTrajectory(endTrajectory);
        while (!(current instanceof EndTrajectory)) {
            if (!(current instanceof FullTrajectory)) {
                throw new Error('Unexpected case');
            }
            const previousTangent = current.getTangentTo(previous);
            const nextTangent = current.getOtherTangent(previousTangent);
            arcs.push(new ChannelPathArc({
                startPoint: previousTangent.getPoint(current),
                startDirection: previousTangent.getReverseDirection(current),
                endPoint: nextTangent.getPoint(current),
                endDirection: nextTangent.getDirection(current),
                center: current.center,
                radius: current.radius,
                rotation: previousTangent.getReverseRotation(current)
            }));
            previous = current;
            current = nextTangent.getOtherTrajectory(current);
        }
        arcs.push(new ChannelPathArc({
            startPoint: current.tangent.getPoint(current),
            startDirection: current.tangent.getReverseDirection(current),
            endPoint: current.point,
            endDirection: [-current.direction[0], -current.direction[1]],
            center: current.center,
            radius: current.radius,
            rotation: -current.rotation
        }));
        return new ChannelPath({
            startPoint: endTrajectory.point,
            endPoint: current.point,
            arcs
        });
    }
}
//# sourceMappingURL=Router.js.map